
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import MfaVerification from '@/components/MfaVerification';
import { markResellerMagicLinkEntry, setResellerSession } from '@/utils/resellerSession';

const VerifyMfa = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, isAdmin } = useAuth();
  const [userEmail, setUserEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [codeSent, setCodeSent] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Reseller portal context
  const isResellerPortalAccess = location.state?.isResellerPortalAccess;
  const resellerOrgId = location.state?.organisationId;
  
  // Use ref to prevent duplicate sends
  const sendingRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate initialization
    if (isInitialized) return;

    const initializeMfa = async () => {
      // Get email from various sources
      const storedCompanyData = sessionStorage.getItem('figabl_company_data');
      const emailFromForm = storedCompanyData ? JSON.parse(storedCompanyData)?.pocEmail : null;
    
      const emailFromState = location.state?.email;
      const emailFromUser = user?.email;

      const email = emailFromState || emailFromForm || emailFromUser;
      
      if (!email) {
        navigate('/step/1');
        return;
      }

      setUserEmail(email);
      
      // Step 1: Check for existing MFA factor
      const { phone: mfaPhone, hasActiveFactor } = await checkUserMfaFactorType(email);
      
      let phoneToUse = mfaPhone;
      
      // Step 2: If no MFA phone found, fetch from database
      if (!phoneToUse) {
        const dbPhone = await fetchPhoneFromDatabase(email);
        if (dbPhone) {
          phoneToUse = dbPhone;
        }
      }

      // No enrolled MFA or no phone — collect phone and enroll first
      if (!hasActiveFactor || !phoneToUse?.trim()) {
        navigate('/setup-mfa', {
          replace: true,
          state: {
            email,
            isResellerPortalAccess: location.state?.isResellerPortalAccess,
            organisationId: location.state?.organisationId,
            isAltEmailEnrollment: location.state?.isAltEmailRecipient,
            primaryEmail: location.state?.primaryEmail,
            submissionIdentifier:
              location.state?.submissionIdentifier ||
              sessionStorage.getItem('figabl_reseller_submission_identifier') ||
              sessionStorage.getItem('figabl_submission_identifier'),
            from: location.state?.from
          }
        });
        return;
      }
      
      // Step 3: Update state with phone number
      setPhoneNumber(phoneToUse);
      sessionStorage.setItem(`figabl_mfa_phone_${email}`, phoneToUse);
      
      // Step 4: Mark as initialized
      setIsInitialized(true);
      
      // Step 5: Send verification code only after phone is resolved
      setTimeout(() => {
        sendVerificationCode(email, phoneToUse);
      }, 100);
    };

    initializeMfa();
  }, [location.state, user, navigate, isInitialized]);

  const checkUserMfaFactorType = async (
    email: string
  ): Promise<{ phone?: string; hasActiveFactor: boolean }> => {
    try {
      
      // Check for active MFA factors for this user
      let query = supabase
        .from('user_mfa_factors')
        .select('factor_type, secret, phone_number')
        .eq('is_active', true)
        .eq('factor_type', 'sms') // Only check for SMS factors

      // If a specific email was passed via state and differs from the auth user's email,
      // look up the factor by email (alt-email recipient case). Otherwise prefer user_id.
      const isAltEmailRecipient = !!location.state?.email && !!user?.email && location.state.email !== user.email;

      if (isAltEmailRecipient && user?.id) {
        query = query.eq('user_id', user.id).eq('secret', email);
      } else if (user?.id) {
        query = query.eq('user_id', user.id);
      } else {
        // For magic link users without user_id, check by email in secret field
        query = query.eq('secret', email);
      }

      const { data, error } = await query.limit(1).single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        console.error('[VerifyMfa] Error checking MFA factor type:', error);
        return { hasActiveFactor: false };
      }
      
      if (data) {        
        return { 
          phone: data.phone_number || undefined,
          hasActiveFactor: true
        };
      }

      return { hasActiveFactor: false };
    } catch (error) {
      console.error('[VerifyMfa] Error checking MFA factor type:', error);
      return { hasActiveFactor: false };
    }
  };

  const fetchPhoneFromDatabase = async (email: string): Promise<string | null> => {
    try {
      // For reseller portal access, fetch phone from organisation by org ID
      if (isResellerPortalAccess && resellerOrgId) {
        console.log('[VerifyMfa] Fetching phone for reseller org:', resellerOrgId);
        const { data: orgData, error: orgError } = await supabase
          .from('organisations')
          .select('poc_phone')
          .eq('id', resellerOrgId)
          .single();

        if (orgError) {
          console.error('[VerifyMfa] Error fetching reseller org phone:', orgError);
        }

        if (orgData?.poc_phone) {
          console.log('[VerifyMfa] Found phone via reseller org lookup');
          return orgData.poc_phone;
        }
      }
      
      // First try using submission_identifier if available
      const submissionIdentifier = sessionStorage.getItem('figabl_submission_identifier') || 
                                   sessionStorage.getItem('figabl_reseller_submission_identifier');
      
      if (submissionIdentifier) {
        console.log('[VerifyMfa] Fetching phone using submission_identifier:', submissionIdentifier);
        const { data: sessionData, error: sessionError } = await supabase
          .from('magic_link_sessions')
          .select('poc_phone, email')
          .eq('submission_identifier', submissionIdentifier)
          .maybeSingle();

        if (sessionError) {
          console.error('[VerifyMfa] Error fetching session by submission_identifier:', sessionError);
        } else if (sessionData) {
          // Verify this session belongs to the current email
          if (sessionData.email === email && sessionData.poc_phone) {
            console.log('[VerifyMfa] Found phone via submission_identifier');
            return sessionData.poc_phone;
          }
        }
      }
      
      // Fallback: try to get phone from magic link session by email
      console.log('[VerifyMfa] Falling back to email-based phone lookup');
      const { data: sessionData, error: sessionError } = await supabase
        .from('magic_link_sessions')
        .select('poc_phone')
        .eq('email', email)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (sessionError) {
        console.error('[VerifyMfa] Error fetching from magic link session by email:', sessionError);
      }

      if (sessionData?.poc_phone) {
        console.log('[VerifyMfa] Found phone via email lookup');
        return sessionData.poc_phone;
      }

      // If not found in session, try organisation table
      const { data: orgData, error: orgError } = await supabase
        .from('organisations')
        .select('poc_phone')
        .eq('poc_email', email)
        .eq('poc_phone_verified', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (orgError) {
        console.error('[VerifyMfa] Error fetching from organisation data:', orgError);
      }

      if (orgData?.poc_phone) {
        console.log('[VerifyMfa] Found phone via organisation lookup');
        return orgData.poc_phone;
      }

      console.log('[VerifyMfa] No phone found for email:', email);
      return null;
    } catch (error) {
      console.error('[VerifyMfa] Error fetching phone from database:', error);
      return null;
    }
  };

  const sendVerificationCode = async (email: string, phone?: string | null) => {
    // Prevent duplicate sends using ref
    if (sendingRef.current || codeSent) {
      return;
    }
    
    sendingRef.current = true;
    
    try {
      
      const requestBody = { 
        email: email,
        factorName: 'SMS Verification',
        factorType: 'sms',
        phoneNumber: phone
      };

      const { data, error } = await invokeFunction(supabase, 'enroll-mfa', {
        body: requestBody
      });

      if (error) {
        console.error('Error sending verification code:', error);
        return;
      }
      
      setCodeSent(true);
    } catch (error) {
      console.error('Error sending verification code:', error);
    } finally {
      // Reset the sending flag after a short delay
      setTimeout(() => {
        sendingRef.current = false;
      }, 1000);
    }
  };

  const handleComplete = async () => {
    // Set MFA completion flag with timestamp for this email
    if (userEmail) {
      sessionStorage.setItem(`figabl_mfa_just_completed_${userEmail}`, Date.now().toString());
    }
    
    // Handle reseller portal access - create session using utility with credentials: 'include'
    if (isResellerPortalAccess && resellerOrgId) {
      try {
        const resellerSubmissionIdentifier =
          sessionStorage.getItem('figabl_reseller_submission_identifier') ||
          sessionStorage.getItem('figabl_submission_identifier') ||
          undefined;

        const success = await setResellerSession(
          resellerOrgId,
          userEmail,
          phoneNumber,
          true,
          resellerSubmissionIdentifier ?? undefined
        );
        
        if (!success) {
          console.error('[VerifyMfa] Failed to create reseller session');
          return;
        }
        
        // Clear temporary reseller session data
        sessionStorage.removeItem('figabl_reseller_org_id');
        sessionStorage.removeItem('figabl_reseller_email');
        sessionStorage.removeItem('figabl_reseller_submission_identifier');
        
        markResellerMagicLinkEntry();
        navigate('/client/portal');
        return;
      } catch (error) {
        console.error('[VerifyMfa] Error creating reseller session:', error);
        return;
      }
    }
    
    // For published organisation access via magic link
    if (location.state?.isPublishedOrgAccess && location.state?.organisationId) {
      navigate(`/step/1`);
      return;
    }
    
    // For first-time users (no auth session), redirect to step 2
    if (!user) {
      navigate('/step/2');
    } else {
      // For authenticated users, redirect appropriately
      let redirectPath = '/step/1'; // default fallback
      
      if (isAdmin) {
        redirectPath = '/admin';
      } else if (location.state?.from?.pathname) {
        redirectPath = location.state.from.pathname;
      }
      
      navigate(redirectPath);
    }
  };

  const handleCancel = async () => {
    // For published organisation access, go back to home
    if (location.state?.isPublishedOrgAccess) {
      navigate('/');
      return;
    }
    
    // For first-time users, go back to step 1
    if (!user) {
      navigate('/step/1');
    } else {
      // For authenticated users, sign out
      try {
        await signOut();
      } catch (error) {
        console.error('[VerifyMfa] Error signing out:', error);
      }
    }
  };

  // Reset sending state when component unmounts
  useEffect(() => {
    return () => {
      sendingRef.current = false;
    };
  }, []);

  const handlePhoneNumberChange = (phone: string) => {
    setPhoneNumber(phone);
    if (userEmail) {
      sessionStorage.setItem(`figabl_mfa_phone_${userEmail}`, phone);
    }
  };

  return (
    <MfaVerification
      onComplete={handleComplete}
      onCancel={handleCancel}
      userEmail={userEmail}
      phoneNumber={phoneNumber}
      codeSent={codeSent}
      allowChangePhone={isResellerPortalAccess}
      skipOrgPhoneUpdate={isResellerPortalAccess}
      onPhoneNumberChange={handlePhoneNumberChange}
    />
  );
};

export default VerifyMfa;
